// deployment_class: customer_env_allowed
/**
 * Where a container's CONTENT comes from — the one seam data enters through.
 *
 * ── Why this exists (E3 2026-08-29) ────────────────────────────────────────
 * E3: *"The dashboard is a lens into the data that's actually held in the
 * database. The template never holds data at all… It's always just the lens."*
 *
 * A module carries two different things and they were tangled together:
 *
 *   SHAPE    key · label · eyebrow · icon · which tabs exist
 *   CONTENT  the apex narrative · KPI values · what-needs-you · cards
 *
 * Shape belongs to the template and is knowable with an empty database.
 * Content belongs to whatever supplies it, and with no supplier there simply
 * IS none. This context is the supplier's socket.
 *
 * ── What it replaces, and why that is better ───────────────────────────────
 * The bare template was `const BARE_TEMPLATE = true` — a hardcoded boolean.
 * It produced the right screen for the wrong reason: the dashboard was empty
 * because a constant said so, not because nothing had supplied anything.
 *
 * With this, empty is a CONSEQUENCE. No provider → no content → the container
 * says so. That is E3's placeholder idea made honest: an empty state that
 * proves the wire, rather than a string that stands in for one.
 *
 * ── And it is the CSA seam, not test scaffolding ───────────────────────────
 * A container a TEST can hand data to is a container T4-A can hand data to —
 * same socket, same shape. A test injects generated data the code has never
 * seen and asserts that what went in comes out, which a mirrored constant can
 * never do. Production later wraps the same provider around a CSA read.
 *
 * Deliberately NOT here: fetching, caching, auth, error handling. This module
 * knows only that content arrives from somewhere else.
 */
import { createContext, useContext, type ReactNode } from 'react';
import type { ModuleKey, ModuleDef, Workflow, TabPanel } from './dashboard-data';
import type { Deal } from './project-data';

/** The content half of a module — everything shape does not cover. */
export type ModuleContent = Pick<ModuleDef, 'title' | 'apex' | 'kpis' | 'needs' | 'cards'>;
// `title` joined the content half on 2026-09-01. It had been classified as
// SHAPE, so `bareModule()` let it through and the dashboard <h1> rendered
// "Riverdale Housing Authority" on production islands. An agency's own name is
// the most customer-specific datum there is — it can only come from a supplier.

/**
 * Everything a supplier can provide. Every field optional: a supplier that
 * knows about KPIs but not workflows is a normal, valid supplier — partial
 * knowledge is the usual case, not an error.
 */
export interface DashboardContent {
  readonly modules?: Partial<Record<ModuleKey, Partial<ModuleContent>>>;
  // NOT readonly: `WorkflowList` takes a mutable array, and a content type that
  // cannot be handed to its consumer is a type that describes nothing.
  readonly workflows?: Partial<Record<ModuleKey, Workflow[]>>;
  readonly tabPanels?: Partial<Record<ModuleKey, Record<string, TabPanel | undefined>>>;
  /**
   * The agency's projects. Content like everything else here — they used to be
   * a constant in `project-data.ts` that rendered raw, which is why every
   * island showed Willow and Phoenix.
   */
  readonly deals?: Record<string, Deal>;
  /** Display order for {@link deals}. */
  readonly dealOrder?: readonly string[];
  /** True when a supplier is present but has nothing yet — distinct from absent. */
  readonly empty?: boolean;
}

/**
 * What a container shows when nothing supplies it.
 *
 * Declared ONCE, positively, next to the seam that supplies the non-empty case
 * — rather than produced by stripping fields off a catalog entry, which is what
 * `bareModule()` used to do. The difference is drift: a stripper must enumerate
 * every content field and silently stops covering any field added later, which
 * is precisely how `title` escaped it. This is a value of type
 * `ModuleContent`, so `tsc` fails the build if the type gains a field this does
 * not answer for.
 */
export const EMPTY_MODULE_CONTENT: ModuleContent = {
  title: '',
  apex: { html: '' },
  kpis: [],
  needs: undefined,
  cards: [],
};

/**
 * `null` means NO SUPPLIER — nothing is wired to this container. That is the
 * default and it is not an error state; it is what a template is before it is
 * bound to anything.
 */
const DashboardContentContext = createContext<DashboardContent | null>(null);

export function DashboardContentProvider({
  content,
  children,
}: {
  content: DashboardContent | null;
  children: ReactNode;
}): JSX.Element {
  return (
    <DashboardContentContext.Provider value={content}>{children}</DashboardContentContext.Provider>
  );
}

/** The content supplied to this container, or `null` when nothing supplies any. */
export function useDashboardContent(): DashboardContent | null {
  return useContext(DashboardContentContext);
}

/**
 * The projects supplied to this container.
 *
 * Returns empty when nothing supplies any — which is what a project grid shows
 * for an agency that has not imported a portfolio yet, and is the honest answer.
 * There is no constant to fall back to on purpose: the fallback WAS the bug.
 */
export function useDeals(): { deals: Record<string, Deal>; order: readonly string[] } {
  const content = useDashboardContent();
  return { deals: content?.deals ?? {}, order: content?.dealOrder ?? [] };
}
